import { ConfirmationResult } from "firebase/auth";
import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import googleIcon from "@assets/images/auth/google.svg";
import { useServices } from "@core/servicesContext";
import { LoginError } from "@domain/auth/authError";
import { messageForFirebaseError } from "@domain/auth/firebaseErrorMessage";
import { CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { useAsync } from "@utils/hooks";
import { Button } from "@ui/components/buttons/button";
import { ErrorContainer } from "@ui/components/containers/errorContainer";
import { IndicatorDataList } from "@ui/components/inputs/indicatorDataList";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import { LostPasswordModal } from "./lostPasswordModal";
import { SignupChannel } from "@ui/pages/loginPage/onboarding/authStep";

interface LoginFormProps {
	onSignup: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSignup }) => {
	const { currentProfileService, trackingService } = useServices();
	const { createModal } = useModalTools();
	const [channel, setChannel] = useState<SignupChannel>("phone");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [indicator, setIndicator] = useState<CountryIndicator>("221");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [code, setCode] = useState("");
	const [confirmObj, setConfirmObj] = useState<ConfirmationResult>();
	const [seconds, setSeconds] = useState(0);
	const [phoneLoading, setPhoneLoading] = useState(false);
	const [fieldError, setFieldError] = useState("");

	useEffect(() => {
		if (seconds <= 0) {
			return;
		}
		const timeout = window.setTimeout(() => setSeconds((current) => current - 1), 1000);
		return () => window.clearTimeout(timeout);
	}, [seconds]);

	const { loading, error, run } = useAsync(async () => {
		await currentProfileService.login("Tooshare", email, password);
		trackingService.enableGA();
		return trackingService.track("login", { provider: "Tooshare" });
	}, [email, password]);

	const {
		run: runGoogle,
		loading: googleLoading,
		error: googleError,
	} = useAsync(async () => {
		await currentProfileService.signIn("Google");
		trackingService.enableGA();
		return trackingService.track("login", { provider: "Google" });
	}, []);

	const clearError = (): void => setFieldError("");

	const sendSms = async (): Promise<void> => {
		const digits = phoneNumber.replace(/\D/g, "");
		if (digits.length < 6) {
			setFieldError(I18n.t("auth.signup.phoneInvalid"));
			return;
		}
		setPhoneLoading(true);
		try {
			const setup = await currentProfileService.setupRecaptcha(`+${indicator}${digits}`);
			if (!setup) {
				setFieldError(I18n.t("auth.error.default"));
				return;
			}
			setConfirmObj(setup.confirm);
			setup.recaptcha.clear();
			setSeconds(30);
		} catch (error) {
			setFieldError(messageForFirebaseError(error));
		} finally {
			setPhoneLoading(false);
		}
	};

	const verifyCode = async (): Promise<void> => {
		if (!confirmObj || code.trim().length !== 6) {
			setFieldError(I18n.t("auth.phoneVerify.invalid"));
			return;
		}
		setPhoneLoading(true);
		try {
			const result = await currentProfileService.checkUserWithPhoneNumber(confirmObj, code.trim());
			if (!result.user) {
				setFieldError(I18n.t("auth.phoneVerify.invalid"));
				return;
			}
			trackingService.enableGA();
			await trackingService.track("login", { provider: "Phone" });
		} catch {
			setFieldError(I18n.t("auth.phoneVerify.invalid"));
		} finally {
			setPhoneLoading(false);
		}
	};

	const fullPhone = `+${indicator} ${phoneNumber.replace(/\D/g, "")}`;
	const shownError = error ?? googleError;

	return (
		<Form
			onSubmit={async (event) => {
				event.preventDefault();
				clearError();
				if (confirmObj) {
					await verifyCode();
					return;
				}
				if (channel === "phone") {
					await sendSms();
					return;
				}
				if (!email || !password) {
					return;
				}
				await run();
			}}
		>
			{!confirmObj && (
				<>
					<Title>{I18n.t("auth.login.title")}</Title>
					<Lead>{I18n.t("auth.login.lead")}</Lead>
					<Button
						variant="social"
						size="lg"
						block
						type="button"
						loading={googleLoading}
						onClick={() => void runGoogle()}
					>
						<img src={googleIcon} alt="" />
						{I18n.t("login.button.google")}
					</Button>
					<Or>
						<span>{I18n.t("auth.signup.or")}</span>
					</Or>
					<Channels>
						<Channel
							type="button"
							$active={channel === "phone"}
							onClick={() => {
								setChannel("phone");
								clearError();
							}}
						>
							{I18n.t("auth.signup.phoneTab")}
						</Channel>
						<Channel
							type="button"
							$active={channel === "email"}
							onClick={() => {
								setChannel("email");
								clearError();
							}}
						>
							{I18n.t("auth.signup.emailTab")}
						</Channel>
					</Channels>
				</>
			)}
			{!confirmObj && channel === "email" && (
				<>
					<Field>
						<Label htmlFor="login-email">{I18n.t("email.placeholder")}</Label>
						<SimpleTextInput
							id="login-email"
							value={email}
							onChange={setEmail}
							placeholder={I18n.t("email.placeholder")}
							type="email"
							autoComplete="email"
						/>
					</Field>
					<Field>
						<Label htmlFor="login-password">{I18n.t("password.placeholder")}</Label>
						<SimpleTextInput
							id="login-password"
							value={password}
							onChange={setPassword}
							placeholder={I18n.t("password.placeholder")}
							type="password"
							autoComplete="current-password"
						/>
					</Field>
					<Forgot type="button" onClick={() => createModal((close) => <LostPasswordModal close={close} />)}>
						{I18n.t("login.password.forgotten")}
					</Forgot>
				</>
			)}
			{!confirmObj && channel === "phone" && (
				<Field>
					<Label htmlFor="login-phone">{I18n.t("register.phonenumber.placeholder")}</Label>
					<PhoneRow>
						<IndicatorDataList value={indicator} onChange={setIndicator} />
						<SimpleTextInput
							id="login-phone"
							value={phoneNumber}
							onChange={(value) => {
								setPhoneNumber(value);
								clearError();
							}}
							placeholder={I18n.t("register.phonenumber.placeholder")}
							type="tel"
							autoComplete="tel-national"
							aria-describedby="login-phone-help"
						/>
					</PhoneRow>
					<Helper id="login-phone-help">{I18n.t("auth.signup.phoneLead")}</Helper>
				</Field>
			)}
			{confirmObj && (
				<>
					<Title>{I18n.t("auth.phoneVerify.title")}</Title>
					<Lead>{I18n.t("auth.phoneVerify.lead", { phone: fullPhone })}</Lead>
					<Field>
						<Label htmlFor="login-otp">{I18n.t("register.phonenumber.otp.label")}</Label>
						<SimpleTextInput
							id="login-otp"
							value={code}
							onChange={(value) => {
								setCode(value.replace(/\D/g, "").slice(0, 6));
								clearError();
							}}
							placeholder="000000"
							type="text"
							autoComplete="one-time-code"
						/>
					</Field>
					<Resend>
						{I18n.t("auth.phoneVerify.resendPrompt")}{" "}
						{seconds > 0 ? (
							<strong>{I18n.t("auth.phoneVerify.resendIn", { seconds })}</strong>
						) : (
							<SwitchLink type="button" onClick={() => void sendSms()} disabled={phoneLoading}>
								{I18n.t("auth.phoneVerify.resend")}
							</SwitchLink>
						)}
					</Resend>
				</>
			)}
			{channel === "phone" && <Recaptcha id="recaptcha-container" />}
			{fieldError && <ErrorContainer>{fieldError}</ErrorContainer>}
			{shownError && (
				<ErrorContainer>
					{shownError.message === LoginError.CREDENTIALS
						? I18n.t("auth.error.credentials")
						: I18n.t("auth.error.default")}
				</ErrorContainer>
			)}
			<Button
				variant="primary"
				size="lg"
				block
				loading={loading || phoneLoading || googleLoading}
				type="submit"
				disabled={!confirmObj && channel === "email" && (!email || !password)}
			>
				{confirmObj ? I18n.t("auth.phoneVerify.submit") : I18n.t("login.button.submit")}
			</Button>
			{!confirmObj && (
				<Switch>
					{I18n.t("auth.login.switchPrompt")}{" "}
					<SwitchLink type="button" onClick={onSignup}>
						{I18n.t("auth.login.switchAction")}
					</SwitchLink>
				</Switch>
			)}
		</Form>
	);
};

const Form = styled.form`
	${gap(16)};
	width: 100%;

	button[type="submit"] {
		margin-top: 8px;
	}
`;

const Title = styled.h1`
	margin: 0 0 6px;
	font-size: 28px;
	font-weight: 700;
	letter-spacing: -0.4px;
	line-height: 1.2;
	color: #13131a;
`;

const Lead = styled.p`
	margin: 0 0 8px;
	color: #8a94a4;
	font-size: 15px;
	line-height: 1.5;
	font-weight: 400;
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 7px;
`;

const Label = styled.label`
	font-size: 13.5px;
	font-weight: 500;
	color: #5b6272;
`;

const Helper = styled.span`
	color: #8a94a4;
	font-size: 13px;
	line-height: 1.45;
`;

const Or = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	color: #8a94a4;
	font-size: 13px;

	&::before,
	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background: #e3e7ec;
	}
`;

const Channels = styled.div`
	display: flex;
	gap: 4px;
	padding: 4px;
	border-radius: 999px;
	background: #e7f4ec;
`;

const Channel = styled.button<{ $active: boolean }>`
	flex: 1;
	height: 40px;
	border: none;
	border-radius: 999px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	color: ${({ $active }) => ($active ? "#00863d" : "#5b6272")};
	background: ${({ $active }) => ($active ? "#ffffff" : "transparent")};
	box-shadow: ${({ $active }) => ($active ? "0 2px 8px rgba(0, 0, 0, 0.08)" : "none")};
`;

const PhoneRow = styled.div`
	display: grid;
	grid-template-columns: 110px 1fr;
	gap: 8px;
	align-items: center;
`;

const Forgot = styled.button`
	align-self: flex-end;
	border: none;
	background: none;
	cursor: pointer;
	padding: 0;
	color: #00863d;
	font-size: 13px;
	font-weight: 600;
`;

const Resend = styled.p`
	margin: 0;
	text-align: center;
	font-size: 14px;
	color: #5b6272;

	strong {
		color: #00863d;
		font-weight: 600;
	}
`;

const Recaptcha = styled.div`
	position: absolute;
	width: 0;
	height: 0;
	overflow: hidden;
`;

const Switch = styled.p`
	margin: 22px 0 0;
	text-align: center;
	font-size: 14px;
	color: #5b6272;
`;

const SwitchLink = styled.button`
	border: none;
	background: none;
	padding: 0;
	cursor: pointer;
	color: #00863d;
	font-size: 14px;
	font-weight: 600;

	&:disabled {
		cursor: default;
		opacity: 0.6;
	}
`;
